import { ChangeDetectionStrategy, Component, computed, Inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { CopyToClipboardService } from 'src/app/services/copy-to-clipboard.service';
import { ReportLogLevel } from 'src/app/services/exam/enums/report-level.enum';
import { ReportExamService } from 'src/app/services/exam/report-exam.service';
import { ExamService } from 'src/app/services/restrictions/exam.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { AsyncPipe, CommonModule } from '@angular/common';

enum ExamDlgOpts {
  Submit = 0,
  Unlock = 1,
  Extend = 2,
}
export interface IExamUnlockDialogResult {
  cause: string;
  extendTime: number;
  continue: boolean;
}

export interface ExamUnlockDialogData {
  goToFullscreenHandler: () => void;
  unlockReason: string;
  testMode: boolean;
}

@Component({
    selector: 'asg-exam-unlock-dialog',
    templateUrl: './exam-unlock-dialog.component.html',
    styleUrls: ['./exam-unlock-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    MatDialogModule,
    MatButtonToggleModule,
    FormsModule,
    MatCheckboxModule,
    MatButtonModule,
    AsyncPipe,
    TranslateModule,
    CommonModule
],
})
export class ExamUnlockDialogComponent implements OnInit, OnDestroy {
  @ViewChild('unlockOrExtendButton') unlockOrExtendButton: MatButton;
  opts = ExamDlgOpts;
  dlgChoice = ExamDlgOpts.Unlock;
  cause = this.data.unlockReason;
  extendTime = 5;
  isSubmit = false;
  isSubmitConfirmed = false;
  enteredCode = '';
  numberOfAttempts = new BehaviorSubject(0);
  testMode: boolean = false;
  submitAttempted = false;

  // timer
  timerCount = signal(0);
  isRedColor = computed(() => this.timerCount() > 300); // if more then 5 min -> red
  formattedTime = computed(() => {
    const totalSeconds = this.timerCount();
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formatted = '';
    if (hours > 0) {
      formatted += `${hours}${this.translate.instant('EXAM_UNLOCK_DIALOG.HOURS_SHORTHAND')} `;
    }
    if (minutes > 0 || hours > 0) { // Include minutes if hours are displayed
      formatted += `${minutes}${this.translate.instant('EXAM_UNLOCK_DIALOG.MINUTES_SHORTHAND')} `;
    }
    if (seconds > 0 || formatted === '') { // Always include seconds if nothing else
      formatted += `${seconds}${this.translate.instant('EXAM_UNLOCK_DIALOG.SECONDS_SHORTHAND')}`;
    }
    return formatted.trim(); // Remove trailing whitespace
  });
  private timerId;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ExamUnlockDialogData,
    private dialogRef: MatDialogRef<ExamUnlockDialogComponent>,
    private examService: ExamService,
    private reportExamService: ReportExamService,
    private readonly translate: TranslateService,
    private readonly copyToClipboardService: CopyToClipboardService
  ) {
    this.testMode = this.data.testMode;
  }

  async ngOnInit(): Promise<void> {
    if (this.testMode) {
      this.numberOfAttempts.next(0);
      return;
    }

    try {
      const attempts = await this.examService.getUnlockCodeAttempts();
      this.numberOfAttempts.next(attempts + 1);
    }
    catch {
      this.numberOfAttempts.next(-1);
    }

    // timer to keep track of time passed
    // use date's because setInterval doesn't trigger when running to long in the background
    const start = new Date();
    this.timerId = setInterval(() => {
      const now = new Date();
      const diff = now.valueOf() - start.valueOf();
      this.timerCount.set(Math.floor(diff / 1000));
    }, 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.timerId);
  }

  confirmSubmit(isChecked: boolean): void {
    this.isSubmitConfirmed = isChecked;
  }

  async unlockOrExtend(): Promise<void> {
    await this.overwriteClipboard()
    this.data.goToFullscreenHandler();
    this.cause = !!this.data.unlockReason ? this.data.unlockReason : this.cause;

    if (!this.areRequiredFieldsFilled()) {
      return;
    }
    this.submitAttempted = false;

    if (this.testMode) {
      const result = this.result(true);
      this.dialogRef.close(result);
      return;
    }

    let codeResult = false;

    if (this.dlgChoice === ExamDlgOpts.Extend) {
      codeResult = await this.extend()
    } else {
      codeResult = await this.unlock();
    }

    if (codeResult) {
      const result = this.result(true);
      this.dialogRef.close(result);
    }
  }

  async submit(): Promise<void> {
    if (!this.areRequiredFieldsFilled()) {
      return;
    }
    this.submitAttempted = false;

    if (this.testMode) {
      const result = this.result(false);
      this.dialogRef.close(result);
      return;
    }

    this.reportExamService.sendTemplateMessageReport(
      ReportLogLevel.Interruptions,
      'WOLAND.REPORT_TEMPLATES.SUBMIT_EXAM_TEMPLATE',
      { comment: this.cause, dialogUpDuration: this.timerCount() });

    await this.examService.submitExam(true).then(() => {
      const result = this.result(false);
      this.dialogRef.close(result);
    }).finally(() => this.cause = '');
  }

  areRequiredFieldsFilled() {
    const isCauseFilled = this.cause != null && this.cause.trim() !== '';
    const isCodeFilled = this.enteredCode != null && this.enteredCode.trim() !== '';
    
    return isCauseFilled
      && ((this.dlgChoice !== this.opts.Submit && isCodeFilled) || this.isSubmitConfirmed);
  }

  private async extend(): Promise<boolean> {
    const codeResult = await this.examService.extendExam(this.enteredCode, this.extendTime);

    await this.reportExamService.sendTemplateMessageReport(
      ReportLogLevel.Interruptions,
      'WOLAND.REPORT_TEMPLATES.EXTENT_TIME_TEMPLATE',
      { time: this.extendTime, comment: !!this.cause ? this.cause : 'none', dialogUpDuration: this.formattedTime() })
      .finally(() => this.cause = '');


    return codeResult;
  }

  private async unlock(): Promise<boolean> {
    const codeResult = await this.examService.checkCode(this.enteredCode);
    if (codeResult) {
      await this.reportExamService.sendTemplateMessageReport(
        ReportLogLevel.Interruptions,
        'WOLAND.REPORT_TEMPLATES.UNLOCK_CODE_TEMPLATE',
        { comment: !!this.cause ? this.cause : 'none' , dialogUpDuration: this.formattedTime()},
      ).finally(() => this.cause = '');
    }
    else{
      await this.reportExamService.sendTemplateMessageReport(
        ReportLogLevel.Interruptions,
        'WOLAND.ERRORS.50'
      ).finally(() => this.cause = '');
    }

    return codeResult;
  }

  private result(unlock: boolean): IExamUnlockDialogResult {
    return {
      cause: this.cause,
      extendTime: this.dlgChoice === ExamDlgOpts.Extend ? this.extendTime : 0,
      continue: unlock
    };
  }

  private async overwriteClipboard(): Promise<void> {
        // Error: Document is not focused
        // This error happens in Chrome when there is delay in writing to clipboard and user is not focusing on the window.
        if (!document.hasFocus()) {
          console.log('Document was not focused, set focus on unlockOrExtendButton');
          this.unlockOrExtendButton.focus();
        }
        // copy default text to clipboard
        const message = this.translate.instant('EXAM_RESTRICTIONS.GENERAL.CLIPBOARD_MESSAGE');
        await this.copyToClipboardService.copy(message)
          .catch((error) => console.error(error));
  }
}
